import { ReadOutlined } from '@ant-design/icons'
import { useDebounce, useRequest } from 'ahooks'
import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Table,
  TablePaginationConfig,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnType } from 'antd/es/table'
import { isNil, omit, toNumber } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'

import { Editor, MosaicComponent, RiskBar, UserAvatar, UsersSearch, getPointPermission } from '@/components'
import { HotResource } from '@/components/hot-resource/hot-resource.component'
import { CommitteeMemberManager } from '@/components/resource-committee-members'
import { routes } from '@/router/router.config'
import { PAGE_RESOURCE_POINTS_ENUM, PriorityEnum, ResourceType } from '@/schemas'
import { useAxiosWithAtom } from '@/utils'
import { ga, traceEvent } from '@/utils/ga'

import { Header } from '../header'
import { FlowStatusButton } from './flow-status/flow-status-button.component'
import { StagesSummery } from './production-detail/production-summary'
import { ProductionHomeExport } from './production-home-export.component'
import { financialOperation, productionIPDCommittee } from './production.atom'
import { Production, ProductionClassification, ProductionFlowStatus, Stage } from './production.schema'

const { Text } = Typography
const { RangePicker } = DatePicker

export const ProductionHome: React.FC<{}> = () => {
  const { t } = useTranslation()
  const [selfIds, setSelfIds] = React.useState<string[]>([])

  React.useEffect(() => {
    ga('event', traceEvent.navigation.events.show_navigation, {
      event_category: traceEvent.navigation.event_categories.ef_pmo,
      event_label: traceEvent.navigation.event_labels.productions,
      action: traceEvent.navigation.action,
    })
  }, [])

  return (
    <>
      <Header
        leftExtra={
          <div>
            <ReadOutlined />
            <Button
              className='pl-1'
              type='link'
              onClick={() =>
                window.open('https://confluence.agoralab.co/pages/viewpage.action?pageId=1271694179', '_blank')
              }
            >
              {t('production.document')}
            </Button>
          </div>
        }
        resourceType={ResourceType.PRODUCTION}
        actions={[<ProductionHomeExport key='export' selfIds={selfIds} />]}
      />
      <div className='pt-2'>
        <CommitteeMemberManager resourceType={ResourceType.PRODUCTION} />
      </div>
      <div className='pb-2'>
        <ProductionList withParamPersist={true} onSelfIdsChange={setSelfIds} />
      </div>
    </>
  )
}

export const ProductionList: React.FC<{
  canExport?: boolean
  isFullWidth?: boolean
  withParamPersist?: boolean
  extraOperationLoading?: boolean
  renderExtras?: any
  onSelfIdsChange?: (selfIds: string[]) => void
}> = ({
  withParamPersist = true,
  isFullWidth = true,
  extraOperationLoading = false,
  renderExtras,
  onSelfIdsChange,
}) => {
  const { t } = useTranslation()
  const history = useHistory()

  const defaultPageSize = 50
  const translateMap = {
    [ProductionFlowStatus.待立项申请]: t('production.flowStatus.ProjectWaitForApply.title'),
    [ProductionFlowStatus.待指派TO]: t('production.flowStatus.TOWaitForAssign.title'),
    [ProductionFlowStatus.待资源预估]: t('production.flowStatus.ResourceWaitForEstimate.title'),
    [ProductionFlowStatus.待立项审批]: t('production.flowStatus.ProjectWaitForAudit.title'),
    [ProductionFlowStatus.研发进行中]: t('production.flowStatus.DevOnGoing.title'),
    [ProductionFlowStatus.待IPD审批]: t('production.flowStatus.IPDWaitForAudit.title'),
    [ProductionFlowStatus.待产品验收]: t('production.flowStatus.ProductWaitForValidate.title'),
    [ProductionFlowStatus.待项目结项]: t('production.flowStatus.ProjectWaitForFinish.title'),
    [ProductionFlowStatus.项目已结项]: t('production.flowStatus.ProjectFinished.title'),
    [ProductionFlowStatus.项目已驳回]: t('production.flowStatus.ProjectRejected.title'),
    [ProductionFlowStatus.项目已关闭]: t('production.flowStatus.ProjectClosed.title'),
  }

  const [expandRows, setExpandRows] = React.useState<number[]>([])
  const [formValue, setFormValue] = React.useState<any>({})
  const [tableRowData, setTableRowData] = React.useState<Production[]>([])
  const [current, setCurrent] = React.useState<number>()
  const [pageSize, setPageSize] = React.useState(defaultPageSize)
  const [total, setTotal] = React.useState(0)
  const [shouldRefresh, setShouldRefresh] = React.useState(false)
  const debouncedNameSearch = useDebounce(formValue.name, { wait: 500 })

  const searchFormRef = React.useRef<{
    form?: FormInstance<any>
    search: (query?: { [key: string]: any }) => void
  }>(null)

  const onFormChange = (formValue: any) => {
    persistSearch({
      page: formValue.page,
      pageSize: formValue.pageSize,
      formValue: omit(formValue, ['page', 'pageSize']),
    })
  }

  const { run: runQueryList, loading } = useRequest(
    ({ params }) => {
      return {
        url: '/api/production/list',
        method: 'get',
        params,
      }
    },
    {
      manual: true,
    }
  )

  const { data: IPDCommittee, loading: ipdCommitteeLoading } = useAxiosWithAtom(productionIPDCommittee, {
    url: '/api/production/ipd/committee',
  })
  const { data: financialOperationAdmin, loading: financialOperationAdminLoading } = useAxiosWithAtom(
    financialOperation,
    {
      url: '/api/production/admin/financial-operation',
    }
  )

  const queryList = (params = {}) => {
    if (!Object.keys(params).length) return

    runQueryList({ params }).then(({ items, pagination: { total } }) => {
      setTotal(total)
      const remoteData = items.map((item: Production) => {
        return {
          ...item,
          key: item.selfId,
        }
      })
      onSelfIdsChange?.(remoteData.map((item: Production) => item.selfId))
      setTableRowData(remoteData)
    })
  }

  React.useEffect(() => {
    if (debouncedNameSearch === formValue.name) {
      queryList(formValue)
    }
  }, [debouncedNameSearch, formValue])

  React.useEffect(() => {
    if (shouldRefresh) {
      queryList(formValue)
      setShouldRefresh(false)
    }
  }, [shouldRefresh])

  const persistSearch = (
    params?: Partial<{
      page: number
      pageSize: number
      formValue: { orderKey?: string; order?: string }
    }>
  ) => {
    if (!withParamPersist) {
      setFormValue(params?.formValue)
      return
    }
    const { page: paramPage, pageSize: paramPageSize, formValue: paramFormValue } = params || {}
    const { page: _page, pageSize: _pageSize, ...value } = queryString.parse(location.search) as any
    const page = !isNil(paramPage) ? paramPage : !isNil(_page) ? _page : 0
    const pageSize = paramPageSize ?? _pageSize ?? defaultPageSize
    const formValue = { ...value, ...paramFormValue }

    const search = { ...formValue, page, pageSize } as any
    search.status = search.status ?? []
    history.replace({
      search: queryString.stringify(Object.assign(queryString.parse(location.search), search)),
    })
    setCurrent(Number(search.page) + 1)
    setPageSize(Number(search.pageSize))
    setFormValue(search)

    return search
  }

  const handleTableChange = ({ current, pageSize }: TablePaginationConfig, _filters: any, sorter: any) => {
    if (pageSize) {
      setPageSize(pageSize)
    }
    if (current) {
      setCurrent(current)
    }
    const page = current ? current - 1 : 0
    const size = pageSize ? pageSize : defaultPageSize
    persistSearch({
      pageSize: size,
      page,
      formValue: { orderKey: sorter?.columnKey, order: sorter?.order },
    })
  }

  const expandDetail = (record: Production & { key: number }) => {
    const key = record.key
    const index = expandRows.indexOf(key)
    if (~index) {
      setExpandRows(expandRows.filter((_, i) => i !== index))
    } else {
      setExpandRows([...expandRows, record.key])
    }
  }

  const getColumnSearchProps = (dataIndex: string): ColumnType<Production> => ({
    render: (record: any) => {
      if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_成员, record.permissions).canRead) {
        return <MosaicComponent />
      }
      const email = record.roles.find((r: any) => r.role === dataIndex)?.email
      return (
        <Text className='flex-grow flex'>
          <UserAvatar showName email={email} />
        </Text>
      )
    },
  })

  const columns = [
    {
      width: 150,
      title: t('production.owner'),
      dataIndex: 'email',
      key: 'email',
      fixed: 'left' as any,
      render: (email: string, record: Production) => {
        return (
          <Text className='flex-grow flex'>
            <RiskBar className='mr-20px' risk={record.risk} />
            <UserAvatar showName email={email} />
          </Text>
        )
      },
    },
    {
      width: 400,
      ellipsis: true,
      title: t('production.name'),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left' as any,
      render: (name: string, record: Production) => {
        return (
          <Link
            className='leading-[30px]'
            onClick={() => {
              window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_productions)
            }}
            to={routes.production.detail(record.selfId)}
          >
            <HotResource selfId={record.selfId} type={record.type} readonly hotData={record.hots} />
            <span className='ml-4'>{name}</span>
          </Link>
        )
      },
    },
    {
      width: 120,
      title: t('createdAt'),
      key: 'createdAt',
      sorter: true,
      render(record: any) {
        return moment(record.createdAt).format('YYYY-MM-DD')
      },
    },
    {
      width: 120,
      title: t('production.detail.classification'),
      key: 'classification',
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_产品分类, record.permissions).canRead ? (
          <MosaicComponent />
        ) : record.classification ? (
          t(`production.form.classification.${ProductionClassification[record.classification]}`)
        ) : null
      },
    },
    {
      width: 250,
      title: t('production.timePeriod'),
      render: (_: any, record: Production) => {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          `${moment(record.startDate).format('YYYY-MM-DD')} - ${
            record.endDate ? moment(record.endDate).format('YYYY-MM-DD') : 'N/A'
          }`
        )
      },
    },
    {
      width: 200,
      title: t('production.predictWorkload.title'),
      key: 'predict_workload', // 字段被后端 order 所使用
      sorter: true,
      render: (_: any, record: Production) => {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_资源预估, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          Number(record.predictWorkload || 0).toFixed(0)
        )
      },
    },
    {
      width: 200,
      title: t('production.actualWorkload.title'),
      key: 'actual_workload', // 字段被后端 order 所使用
      sorter: true,
      render: (_: any, record: Production) => {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          Number(record.actualWorkload).toFixed(0)
        )
      },
    },
    {
      width: 120,
      title: t('devlop.detail.priority'),
      key: 'priority',
      sorter: true,
      render(record: any) {
        return t(`form.priority.${PriorityEnum[record.priority]}`)
      },
    },
    {
      width: 120,
      title: t('production.TO'),
      key: 'to',
      sorter: true,
      ...getColumnSearchProps('TO'),
    },
    {
      width: 120,
      title: t('production.detail.productionStage'),
      key: 'stage',
      sorter: true,
      render(record: any) {
        return !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_产品阶段, record.permissions).canRead ? (
          <MosaicComponent />
        ) : (
          t(`production.stage.${record.stage}`)
        )
      },
    },
    {
      width: 120,
      title: t('production.status.title'),
      key: 'flowStatus',
      sorter: true,
      render(record: any) {
        if (!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, record.permissions).canRead) {
          return <MosaicComponent />
        }

        if (record.flowStatus === ProductionFlowStatus.研发进行中 && record.confirmedDeliveryTime) {
          const isWithinTime = moment().utc().isBefore(moment(record.confirmedDeliveryTime).utc(), 'date')
          return (
            <span className={isWithinTime ? 'text-green-500' : 'text-red-500'}>
              <Tooltip
                visible={isWithinTime ? false : undefined}
                title={t('production.flowStatus.DevOnGoing.table.tooltip', {
                  date: moment(record.confirmedDeliveryTime).utc().format('YYYY-MM-DD'),
                })}
              >
                {translateMap[record.flowStatus as keyof typeof translateMap]}
              </Tooltip>
            </span>
          )
        }

        if ([ProductionFlowStatus.待产品验收, ProductionFlowStatus.待项目结项].includes(record.flowStatus)) {
          const isWithinTime = moment().utc().isBefore(moment(record.endDate).utc(), 'date')
          return (
            <div>
              <Tooltip
                visible={isWithinTime ? false : undefined}
                title={t('production.flowStatus.ProductWaitForValidate.table.tooltip', {
                  date: moment(record.endDate).utc().format('YYYY-MM-DD'),
                })}
              >
                <span className={isWithinTime ? 'text-green-500' : 'text-red-500'}>
                  {translateMap[record.flowStatus as keyof typeof translateMap]}
                </span>
              </Tooltip>
            </div>
          )
        }

        return translateMap[record.flowStatus as keyof typeof translateMap]
      },
    },
  ]

  if (isFullWidth) {
    columns.push({
      width: 200,
      title: t('production.operation.title'),
      fixed: 'right' as any,
      dataIndex: '',
      key: 'x',
      render: (production: Production & { productionVps: string[] } & { key: number }) => (
        <>
          {~expandRows.indexOf(production.key) ? (
            <Button onClick={() => expandDetail(production)} type='link' size='small'>
              {t('production.operation.close')}
            </Button>
          ) : (
            <Button onClick={() => expandDetail(production)} type='link' size='small'>
              {t('production.operation.open')}
            </Button>
          )}

          {!ipdCommitteeLoading && !financialOperationAdminLoading && (
            <FlowStatusButton
              ipdCommittee={IPDCommittee}
              financialOperationAdmin={financialOperationAdmin}
              production={production}
              refresh={() => setShouldRefresh(true)}
            />
          )}
        </>
      ),
    } as any)
  }

  if (renderExtras) {
    columns.push({
      align: 'center',
      width: 80,
      render: renderExtras,
    } as any)
  }

  const ProductionExtendDetail: React.FC<{ production: Production }> = ({ production }) => {
    const flows = production.flows || []
    const additions = production.additions || []

    const hasProjectWaitForFinishFLow = flows.some((f) => f.status === ProductionFlowStatus.待项目结项)

    // IPD评审意见
    const ipdReviewComment = flows
      .find((f) => f.status === ProductionFlowStatus.研发进行中)
      ?.additions.find((a) => a.type === 'reviewComment')?.value
    // 项目阶段的时间周期
    const stages = additions.filter((a) => a.type === 'Stage')
    // 期望的目标完成时间
    const expectDeliveryTime = production.expectDeliveryTime
    // 确认：目标完成时间
    const confirmedTargetCompleteTime = flows
      .find((f) => f.status === ProductionFlowStatus.待项目结项)
      ?.additions.find((a) => a.type === 'confirmedTargetCompleteTime')?.value
    // 是否达到交付目标
    const deliverTargetReason = flows
      .find((f) => f.status === ProductionFlowStatus.待项目结项)
      ?.additions.find((a) => a.type === 'deliverTargetReason')?.value
    // 是否按时交付
    const deliverTimeReason = flows
      .find((f) => f.status === ProductionFlowStatus.待项目结项)
      ?.additions.find((a) => a.type === 'deliverTimeReason')?.value
    // 是否在预期资源内交付
    const predictResourceReason = flows
      .find((f) => f.status === ProductionFlowStatus.待项目结项)
      ?.additions.find((a) => a.type === 'predictResourceReason')?.value

    return (
      <Descriptions bordered style={{ background: 'white' }} column={6}>
        <Descriptions.Item label={`IPD${t('production.flowStatus.IPDWaitForAudit.IPDPass.form.title')}`} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_审批意见, production.permissions).canRead}
            content={ipdReviewComment}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('production.form.stageTargetTitle')} span={6}>
          {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead ? (
            <MosaicComponent />
          ) : stages.length > 0 ? (
            <StagesSummery stages={stages} production={production} />
          ) : null}
        </Descriptions.Item>

        <Descriptions.Item
          label={t('production.flowStatus.ProductWaitForValidate.pass.form.confirmedTargetCompleteTimeTitle')}
          span={3}
        >
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_确认交付时间, production.permissions).canRead}
            content={
              confirmedTargetCompleteTime ? moment(confirmedTargetCompleteTime).utc().format('YYYY-MM-DD') : undefined
            }
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('production.detail.expectTargetCheckTime')} span={3}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_预计交付时间, production.permissions).canRead}
            content={expectDeliveryTime ? moment(expectDeliveryTime).utc().format('YYYY-MM-DD') : undefined}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('production.flowStatus.ProductWaitForValidate.pass.form.targetTitle')} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, production.permissions).canRead}
            content={
              hasProjectWaitForFinishFLow
                ? deliverTargetReason
                  ? `${t('no')},` + deliverTargetReason
                  : t('yes')
                : undefined
            }
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('production.flowStatus.ProductWaitForValidate.pass.form.timeTitle')} span={6}>
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, production.permissions).canRead}
            content={
              hasProjectWaitForFinishFLow
                ? deliverTimeReason
                  ? `${t('no')},` + deliverTimeReason
                  : t('yes')
                : undefined
            }
          />
        </Descriptions.Item>
        <Descriptions.Item
          label={t('production.flowStatus.ProductWaitForValidate.pass.form.predictResourceTitle')}
          span={6}
        >
          <Editor
            editable={false}
            canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, production.permissions).canRead}
            content={
              hasProjectWaitForFinishFLow
                ? predictResourceReason
                  ? `${t('no')},` + predictResourceReason
                  : t('yes')
                : undefined
            }
          />
        </Descriptions.Item>
      </Descriptions>
    )
  }

  return (
    <div>
      <ProductionSearchForm ref={searchFormRef} onChange={onFormChange} />
      <Table
        className='overflow-x-hidden'
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        pagination={{
          showSizeChanger: true,
          total,
          pageSize,
          current,
          showTotal: (total) => t(`production.count`, { count: total }),
        }}
        columns={columns}
        loading={loading || extraOperationLoading}
        dataSource={tableRowData}
        expandable={{
          expandedRowKeys: expandRows,
          expandRowByClick: true,
          expandedRowRender: (production) => <ProductionExtendDetail production={production} />,
          expandIcon: () => null,
          columnWidth: 0,
        }}
      />
    </div>
  )
}

const searchType = [
  {
    label: 'all',
    value: 0,
  },
  {
    label: 'owner',
    value: 1,
  },
  {
    label: 'TO',
    value: 2,
  },
  {
    label: 'join in',
    value: 3,
  },
]

const ProductionSearchForm = React.forwardRef<
  any,
  {
    onChange: (value: any) => void
  }
>(({ onChange, ...props }, ref) => {
  const { t } = useTranslation()

  const translateMap = {
    [ProductionFlowStatus.待立项申请]: t('production.flowStatus.ProjectWaitForApply.title'),
    [ProductionFlowStatus.待指派TO]: t('production.flowStatus.TOWaitForAssign.title'),
    [ProductionFlowStatus.待资源预估]: t('production.flowStatus.ResourceWaitForEstimate.title'),
    [ProductionFlowStatus.待立项审批]: t('production.flowStatus.ProjectWaitForAudit.title'),
    [ProductionFlowStatus.研发进行中]: t('production.flowStatus.DevOnGoing.title'),
    [ProductionFlowStatus.待IPD审批]: t('production.flowStatus.IPDWaitForAudit.title'),
    [ProductionFlowStatus.待产品验收]: t('production.flowStatus.ProductWaitForValidate.title'),
    [ProductionFlowStatus.待项目结项]: t('production.flowStatus.ProjectWaitForFinish.title'),
    [ProductionFlowStatus.项目已结项]: t('production.flowStatus.ProjectFinished.title'),
    [ProductionFlowStatus.项目已驳回]: t('production.flowStatus.ProjectRejected.title'),
    [ProductionFlowStatus.项目已关闭]: t('production.flowStatus.ProjectClosed.title'),
  }

  const [form] = Form.useForm<any>()

  const search = (query: any = {}) => {
    const { page, pageSize } = queryString.parse(location.search)
    const { range, ...other } = form.getFieldsValue()
    const values = {
      page,
      pageSize,
      ...other,
      ...query,
      startDate: range ? range[0]?.format('YYYY-MM-DD') : '',
      endDate: range ? range[1]?.format('YYYY-MM-DD') : '',
    }
    return onChange(values)
  }

  React.useImperativeHandle(ref, () => ({
    search,
    form,
  }))

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, pageSize, ...value } = queryString.parse(location.search)
    let flowStatus = Object.values(ProductionFlowStatus).filter(
      (v) =>
        ![ProductionFlowStatus.项目已驳回, ProductionFlowStatus.项目已结项, ProductionFlowStatus.项目已关闭].includes(v)
    )
    if (!isNil(page)) {
      flowStatus = value.flowStatus as ProductionFlowStatus[]
    }

    const startDate = value.startDate ? moment(value.startDate) : undefined
    const endDate = value.endDate ? moment(value.endDate) : undefined
    const formData = {
      email: value.email || undefined,
      name: value.name || undefined,
      range: [startDate, endDate],
      searchType: value.searchType ? Number(value.searchType) : 1,
      stage: value.stage || undefined,
      classification: value.classification
        ? Array.isArray(value.classification)
          ? value.classification.map((v) => Number(v))
          : [Number(value.classification)]
        : undefined,
      flowStatus,
    }

    form.setFieldsValue(formData)

    const { range, ...other } = formData
    const values = {
      page,
      pageSize,
      ...other,
      startDate: range ? range[0]?.format('YYYY-MM-DD') : '',
      endDate: range ? range[1]?.format('YYYY-MM-DD') : '',
    }
    onChange(values)
  }, [])

  return (
    <div className='w-full py-4'>
      <Form {...props} form={form} name='productionSearch' layout='inline' onFieldsChange={() => search({ page: 0 })}>
        <Row className='w-full' gutter={[{ xs: 4, sm: 8, md: 16, lg: 24 }, 16]}>
          <Col span={8}>
            <Form.Item name='name'>
              <Input placeholder={t('production.name')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Input.Group compact className='!flex'>
              <Form.Item name='email' className='!flex-1'>
                <UsersSearch placeholder={t('production.owner')} />
              </Form.Item>
              <Form.Item name='searchType' className='w-100px'>
                <Select
                  options={searchType.map((s) => {
                    return {
                      label: t(s.label),
                      value: s.value,
                    }
                  })}
                />
              </Form.Item>
            </Input.Group>
          </Col>
          <Col span={8}>
            <Form.Item name='stage'>
              <Select
                placeholder={t('production.detail.productionStage')}
                allowClear
                mode='multiple'
                options={Object.entries(Stage).map(([, value]) => {
                  return {
                    label: t(`production.stage.${value}`),
                    value: value,
                  }
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className='mt-10px w-full' gutter={[{ xs: 4, sm: 8, md: 16, lg: 24 }, 16]}>
          <Col span={8}>
            <Form.Item required name='range'>
              <RangePicker className='w-full' format='YYYY-MM-DD' />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='flowStatus'>
              <Select
                placeholder={t('production.status.title')}
                allowClear
                mode='multiple'
                options={Object.keys(translateMap).map((c) => ({
                  value: c,
                  label: translateMap[c as keyof typeof translateMap],
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name='classification'>
              <Select
                placeholder={t('production.detail.classification')}
                allowClear
                mode='multiple'
                options={Object.keys(ProductionClassification)
                  .filter((v) => isNaN(toNumber(v)))
                  .map((key) => {
                    return {
                      label: t(`production.form.classification.${key}`),
                      value: ProductionClassification[key as keyof typeof ProductionClassification],
                    }
                  })}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  )
})
